import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CompanyContext = createContext(null);

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
};

export const CompanyProvider = ({ children }) => {
  const [companies, setCompanies] = useState([
    { id: 'blueskies-farm', name: 'Blue Skies Farm LLC', fiscal_year: '2026' },
    { id: 'sunrise-ranch', name: 'Sunrise Ranch Co.', fiscal_year: '2026' },
    { id: 'green-valley', name: 'Green Valley Farms', fiscal_year: '2026' },
  ]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const { isAuthenticated } = useAuth();

  // Real clients live on the server (added in Import All or set up in Company Setup); merge them into the switcher for every signed-in user.
  const refreshCompanies = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/company-profile`, { credentials: 'include' });
      if (!res.ok) return;
      const remote = await res.json();
      setCompanies((prev) => {
        const have = new Set(prev.map((c) => c.id));
        const added = remote.filter((c) => c.id && !have.has(c.id)).map((c) => ({ id: c.id, name: c.name || c.id, fiscal_year: c.fiscal_year || '2026' }));
        return added.length ? [...prev, ...added] : prev;
      });
    } catch (e) { /* offline: keep the built-in list */ }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    refreshCompanies();
    window.addEventListener('companiesChanged', refreshCompanies);
    return () => window.removeEventListener('companiesChanged', refreshCompanies);
  }, [isAuthenticated, refreshCompanies]);

  // Once the server's clients have arrived, restore the company the user last chose (it may be one of them).
  useEffect(() => {
    const saved = localStorage.getItem('selectedCompanyId');
    const wanted = companies.find((c) => c.id === saved);
    if (wanted && selectedCompany && selectedCompany.id !== wanted.id) setSelectedCompany(wanted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies]);

  const addClient = async (name) => {
    const fd = new FormData();
    fd.append('name', name);
    const res = await fetch(`${API_URL}/api/migration/clients`, { method: 'POST', credentials: 'include', body: fd });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Could not add the client');
    setCompanies((prev) => (prev.some((c) => c.id === data.id) ? prev : [...prev, { id: data.id, name: data.name, fiscal_year: data.fiscal_year || '2026' }]));
    return data;
  };

  useEffect(() => {
    // Load saved company from localStorage
    const savedCompanyId = localStorage.getItem('selectedCompanyId');
    if (savedCompanyId) {
      const company = companies.find(c => c.id === savedCompanyId);
      if (company) {
        setSelectedCompany(company);
      } else {
        setSelectedCompany(companies[0]);
      }
    } else {
      setSelectedCompany(companies[0]);
    }
  }, []);

  const switchCompany = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    if (company) {
      setSelectedCompany(company);
      localStorage.setItem('selectedCompanyId', companyId);
      // Trigger a refresh of all data by dispatching a custom event
      window.dispatchEvent(new CustomEvent('companyChanged', { detail: company }));
    }
  };

  // A company just saved on the server: add it to the switcher and make it the one on screen.
  const registerCompany = (c) => {
    const company = { id: c.id, name: c.name, fiscal_year: c.fiscal_year || '2026' };
    setCompanies((prev) => (prev.some((x) => x.id === company.id) ? prev.map((x) => (x.id === company.id ? { ...x, ...company } : x)) : [...prev, company]));
    setSelectedCompany(company);
    localStorage.setItem('selectedCompanyId', company.id);
    window.dispatchEvent(new CustomEvent('companyChanged', { detail: company }));
    return company;
  };

  const addCompany = (company) => {
    const newCompany = {
      id: company.name.toLowerCase().replace(/\s+/g, '-'),
      name: company.name,
      fiscal_year: company.fiscal_year || '2026',
    };
    setCompanies(prev => [...prev, newCompany]);
    return newCompany;
  };

  return (
    <CompanyContext.Provider value={{
      companies,
      selectedCompany,
      switchCompany,
      addCompany,
      addClient,
      registerCompany,
      refreshCompanies,
      companyId: selectedCompany?.id,
    }}>
      {children}
    </CompanyContext.Provider>
  );
};

export default CompanyContext;
